import pica from 'pica';

/**
 * Resize an image file with Pica before uploading to S3
 *
 * @param {Object} options
 * @param {File} options.file - The image file
 * @param {string} options.path - type of image
 * @param {number} options.maxWidth - The maximum width of the image
 * @param {number} options.maxHeight - The maximum height of the image
 * @param {{ width: number, height: number }} options.size - Target width/height
 * @returns {Promise<File>} Resized File ready for upload
 */
export async function resizeImage({
  file,
  path = 'image',
  maxWidth = 900,
  maxHeight = 3000,
  dynamicWidth = 2.5,
}) {
  return new Promise(async (resolve, reject) => {
    // -------- validate --------
    if (!file) {
      reject(new Error('File is required'));
    }
    if (!file.type.includes('image')) {
      reject(new Error('Only image is allowed'));
    }

    // dont resize gif, svg files
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
      // limits in MB
      const sizeLimits = {
        signatures: 2,
        logo: 2,
        profile: 2,
        testimonial: 2,
        covers: 1.5,
        attachments: 3,
        media: 3,
        sections: 3,
        image: 5,
        externalproposals: 10,
      };

      const limit = sizeLimits[path];
      if (limit && file.size > limit * 1000000) {
        reject(new Error(`The file is too large. You can upload images up to ${limit}MB.`));
      }
      return resolve(file);
    }
    // -------- END of validate --------

    // before resize check the file size
    if (file.size > 10 * 1000000) {
      reject(new Error(`The file is too large. You can upload images up to 10MB.`));
    }

    // Files that are already small gain almost nothing from resizing and only
    // lose sharpness
    const SKIP_RESIZE_THRESHOLD = 500 * 1024; // 500KB (matches Uploader)
    if (file.size <= SKIP_RESIZE_THRESHOLD) {
      return resolve(file);
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = async () => {
      try {
        // Check Brave Browser compatibility
        const testCanvas = document.createElement('canvas');
        testCanvas.width = 1;
        testCanvas.height = 1;
        const testCtx = testCanvas.getContext('2d');

        // check if getImageData is available
        let canvasSupported = true;
        try {
          testCtx.getImageData(0, 0, 1, 1);
        } catch (e) {
          if (e.message && e.message.includes('getImageData')) {
            console.warn('Image editing is not supported. Using original file.');
            canvasSupported = false;
          } else {
            throw e;
          }
        }

        if (!canvasSupported) {
          // return original file
          resolve(file);
          return;
        }

        // Source canvas
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = img.width;
        sourceCanvas.height = img.height;
        const ratio = img.width / img.height;
        const sourceCtx = sourceCanvas.getContext('2d');
        sourceCtx.drawImage(img, 0, 0);

        // REMARK : dynamic width based on ratio
        // Wide/banner images (e.g. 4:1 headers) render close to full width, so
        // downscaling them softens edges/text badly. Give them extra width —
        // bytes stay low anyway because the height is small.
        if (dynamicWidth && ratio > dynamicWidth) {
          /*
            The wider the image, the more we raise maxWidth.
            Examples with default maxWidth = 900:
              ratio = 2.6, then scale = 1.03, maxWidth = 927
              ratio = 4,   then scale = 1.45, maxWidth = 1305
              ratio = 5,   then scale = 1.75, maxWidth = 1575
              ratio = 8,   then scale = 2.00 (capped), maxWidth = 1800
          */
          const scale = 1 + (ratio - dynamicWidth) * 0.3;
          maxWidth = Math.round(maxWidth * Math.min(scale, 2));
        }

        // Target canvas
        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = Math.min(img.width, maxWidth);
        targetCanvas.height = targetCanvas.width / ratio;

        // max height could be 900 x 3000
        if (targetCanvas.height > maxHeight) {
          targetCanvas.height = maxHeight;
          targetCanvas.width = maxHeight * ratio;
        }

        // Check if resizing is needed
        if (
          sourceCanvas.width === targetCanvas.width &&
          sourceCanvas.height === targetCanvas.height
        ) {
          resolve(file);
          return;
        }

        const MIN_COMPRESSION_RATIO = 0.2; // don't allow more than 80% size reduction

        const picaInstance = pica();
        await picaInstance.resize(sourceCanvas, targetCanvas);

        // Convert to blob
        const type = file.type || 'image/jpeg';
        let blob = await picaInstance.toBlob(targetCanvas, type, 0.85);

        // if compression was too aggressive, retry at higher quality
        if (blob.size / file.size < MIN_COMPRESSION_RATIO) {
          blob = await picaInstance.toBlob(targetCanvas, type, 0.92);
        }

        // Create a new File
        const resizedFile = new File([blob], file.name, { type });

        resolve(resizedFile);
      } catch (err) {
        console.warn('Image editing is not supported. Using original file.', err.message);
        resolve(file);
      }
    };

    img.onerror = (err) => reject(err);
  });
}
